import { useState, useEffect, useRef } from "react";
import { X, Loader2, PenTool, Check, AlertTriangle, Undo2, ClipboardCheck, FileText as FileIcon, Download, Mail } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import { AttachmentGallery } from "@/components/PhotoUpload";
import EmailModal from "@/components/EmailModal";
import BucketEditor from "@/components/BucketEditor";
import { api, apiError } from "@/lib/api";
import { downloadReportPdf } from "@/lib/reportPdf";
import { signatureSrc } from "@/lib/signatures";
import ReportDocumentView from "@/components/ReportDocumentView";

/**
 * DetailDrawer — inspects any Pit Link resource in a right-side drawer with:
 *  - Quick facts + status
 *  - Attached PHOTOS and DOCUMENTS (PDF viewer/download)
 *  - Inspection checklist items (question / answer / photo rows)
 *  - Supervisor sign-off (approve / complete / sign) via signature pad
 *  - Optional "SEND BACK" secondary action for inspections and PPFs
 *  - Raw JSON for power users
 */
export default function DetailDrawer({
  item,
  onClose,
  onUpdated,
  itemLabel,
  signOffConfig,
  sendBackConfig,
  amendConfig,
  resourceName = "Record",
  openSignOffOnMount = false,
  extraDetail = null,
  documentStyle = false,   // when true, hides the generic FactList, photos, and
                           // existing-signatures panels — the caller is providing
                           // a self-contained document view via extraDetail.
}) {
  const [action, setAction] = useState(openSignOffOnMount ? "sign" : null); // 'sign' | 'send_back' | null
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [signature, setSignature] = useState(null);
  const [notes, setNotes] = useState("");
  const [attach, setAttach] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const scrollRef = useRef(null);
  const signoffRef = useRef(null);

  // Whenever a NEW item is opened in the drawer, snap the scroll position
  // back to the top so the user always sees the header (title + actions)
  // of whatever they just clicked — no need to hand-scroll up.
  useEffect(() => {
    if (item && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    // Only reset scroll on record change — we don't want the drawer scrolling
    // itself every time `item` object identity changes mid-render.
  }, [item?.id, item?._id]);

  // A record can already be signed off — in that case we shouldn't nag the
  // supervisor with a big "Sign to Approve" button in the sticky bar. The
  // caller-side signOffConfig defines an optional `alreadySigned(item)`
  // predicate to short-circuit both the sticky jump button and the full
  // sign-off panel below.
  const alreadySigned = item && signOffConfig?.alreadySigned ? !!signOffConfig.alreadySigned(item) : false;

  // Jump straight to the sign-off block and open the "sign" form so the
  // supervisor never has to scroll past inspection details to find the
  // next-step button.
  const jumpToSignOff = () => {
    setAction("sign");
    // Give React a tick to render the expanded form before scrolling.
    requestAnimationFrame(() => {
      signoffRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  if (!item) return null;

  // PPFs can only be downloaded as a PDF once they've been signed off / approved,
  // matching the mobile app's "PDF once approved" contract.
  const isPpf = String(resourceName).toLowerCase().includes("ppf");
  const ppfApproved = isPpf && (
    /approved|signed|complete|closed|resolved|done/.test(String(item.status || item.stage || "").toLowerCase()) ||
    item.approved_at || item.completed_at
  );
  const pdfDisabled = pdfBusy || (isPpf && !ppfApproved);
  const pdfTitle = isPpf && !ppfApproved
    ? "PDF becomes available once this Planning Request is approved"
    : "Download PDF";

  const onDownload = async () => {
    if (pdfDisabled) return;
    setPdfBusy(true);
    setMsg(null);
    try {
      await downloadReportPdf(item, resourceName);
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally {
      setPdfBusy(false);
    }
  };

  const doSubmit = async (mode) => {
    const cfg = mode === "sign" ? signOffConfig : sendBackConfig;
    if (!cfg) return;
    if (mode === "sign" && cfg.requireSignature !== false && !signature) {
      setMsg({ type: "err", text: "Please draw your signature before submitting." });
      return;
    }
    if (mode === "send_back" && !notes.trim()) {
      setMsg({ type: "err", text: "Please enter a reason before sending back." });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const url = cfg.endpoint(item);
      // Prefer a resource-specific payload builder if provided
      const body = cfg.payload
        ? cfg.payload({ signature, notes, attach, item })
        : {
            signature,
            signature_image: signature,
            supervisor_sig: signature,
            completed_by_sig: signature,
            notes,
            reason: notes,
            comment: notes,
            signed_at: new Date().toISOString(),
            approved: mode === "sign",
            photos: attach.map((a) => a.data),
            attachments: attach,
          };
      const { data } = await api.post(url, body);
      setMsg({
        type: "ok",
        text: mode === "sign"
          ? `Signed off — ${cfg.verb || "approved"}.`
          : "Sent back to originator.",
      });
      onUpdated?.(data);
      setAction(null);
      setSignature(null);
      setNotes("");
      setAttach([]);
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally {
      setBusy(false);
    }
  };

  // Detect inspection-ish payloads
  const inspectionItems = detectChecklist(item);
  const photos = collectPhotos(item);
  const pdfs = collectPdfs(item);

  return (
    <div
      className={`fixed inset-0 z-50 bg-black/70 flex ${documentStyle ? "items-stretch justify-stretch" : "items-end md:items-stretch md:justify-end"}`}
      onClick={onClose}
      data-testid="detail-drawer"
    >
      <div
        ref={scrollRef}
        className={`w-full ${documentStyle ? "md:h-full" : "md:max-w-3xl md:h-full"} panel-raised overflow-auto`}
        style={{ borderRadius: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="sticky top-0 z-10 flex items-center justify-between p-5"
          style={{ borderBottom: "1px solid var(--border)", background: "var(--surface-2)" }}
        >
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">{resourceName}</div>
            <div className="font-display font-black text-lg tracking-tight truncate">
              {itemLabel ? itemLabel(item) : item.title || item.name || "Details"}
            </div>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} data-testid="detail-close">
            <X className="w-4 h-4" /> Close
          </button>
        </div>

        {/* Sticky 1-tap action bar */}
        <div className="sticky top-[73px] z-10 flex flex-wrap items-center gap-2 px-5 py-3"
             style={{ borderBottom: "1px solid var(--border)", background: "var(--surface)" }}>
          {signOffConfig && !alreadySigned && (
            <button
              className="btn btn-sm btn-primary"
              onClick={jumpToSignOff}
              data-testid="btn-jump-signoff"
              title="Jump to sign-off"
            >
              <PenTool className="w-4 h-4" />
              {signOffConfig.label || `Sign to ${signOffConfig.verb || "Approve"}`}
            </button>
          )}
          {signOffConfig && alreadySigned && (
            <div
              className="pill pill-green !text-[10px] !py-1 !px-2 font-black uppercase tracking-widest"
              data-testid="pill-already-signed"
            >
              <Check className="w-3 h-3" /> Signed
            </div>
          )}
          <button
            className="btn btn-sm btn-ghost"
            onClick={onDownload}
            disabled={pdfDisabled}
            title={pdfTitle}
            data-testid="btn-download-pdf"
          >
            {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {pdfBusy ? "Building…" : (isPpf && !ppfApproved ? "PDF (once approved)" : "Download PDF")}
          </button>
          <button className="btn btn-sm btn-ghost" onClick={() => setEmailOpen(true)} data-testid="btn-email-pdf">
            <Mail className="w-4 h-4" /> Email
          </button>
          <div className="flex-1" />
          <div className="text-[10px] uppercase tracking-widest text-muted">
            {signOffConfig && !alreadySigned ? "Next step ↑" : "1-Tap Share"}
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Quick bucket-number editor for digger-family assets — surfaces
              the most-changed field so techs don't have to re-register the
              asset to swap the bucket / GET set. */}
          {String(resourceName).toLowerCase() === "asset" && isBucketedAssetItem(item) && (
            <div
              className="panel-flat p-4 flex items-center justify-between gap-3"
              data-testid="asset-bucket-quick-edit"
            >
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted">Bucket / GET set</div>
                <div className="text-sm text-white/80 mt-0.5">Tap the value to change it — no re-registration needed.</div>
              </div>
              <BucketEditor
                assetId={item.id || item.asset_id}
                value={item.bucket_number}
                onSaved={() => onUpdated?.()}
                size="md"
              />
            </div>
          )}

          {!documentStyle && <FactList item={item} />}

          {/* Optional caller-supplied detail block — used for resource-specific
              renderings (e.g. PPF parts list + tradesman panel) that don't fit
              into the generic FactList shape. When `documentStyle` is on and
              the caller didn't supply an `extraDetail`, we still render the
              certificate-style layout via the generic auto-doc so every new
              resource gets the same look-and-feel for free (Josh's Feb 23
              "same setout for all sections going forward" spec). */}
          {extraDetail
            ? <div data-testid="detail-drawer-extra">{extraDetail}</div>
            : (documentStyle && (
                <div data-testid="detail-drawer-extra">
                  <ReportDocumentView
                    type={(resourceName || "record").toString().toLowerCase()}
                    item={item}
                  />
                </div>
              ))
          }

          {/* Inspection checklist */}
          {inspectionItems.length > 0 && (
            <section className="panel-flat p-5" data-testid="inspection-items">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardCheck className="w-4 h-4 text-primary-brand" />
                <div className="font-display font-black uppercase tracking-widest text-sm">
                  Inspection items ({inspectionItems.length})
                </div>
              </div>
              <div className="divide-border">
                {inspectionItems.map((row, i) => (
                  <div key={i} className="py-3 first:pt-0">
                    <div className="flex items-start gap-3">
                      <div className="text-xs font-mono-tight text-muted pt-0.5 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-black text-sm">
                          {row.question || row.label || row.name || "Item"}
                        </div>
                        <div className="text-sm text-muted mt-1 whitespace-pre-wrap">
                          {formatAnswer(row.answer ?? row.value ?? row.result)}
                        </div>
                        {row.comment && (
                          <div className="text-xs text-muted mt-1 italic">&ldquo;{row.comment}&rdquo;</div>
                        )}
                      </div>
                      <ItemStatusPill v={row.answer ?? row.value ?? row.result ?? row.status} />
                    </div>
                    {row.photo_url && (
                      <img src={row.photo_url} alt="" className="mt-2 ml-8 rounded-lg w-24 h-24 object-cover" style={{ border: "1px solid var(--border)" }} />
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Attachments (photos + PDFs) */}
          {!documentStyle && (photos.length > 0 || pdfs.length > 0) && (
            <section className="panel-flat p-5" data-testid="attachments">
              <AttachmentGallery items={photos} pdfs={pdfs} />
            </section>
          )}

          {/* Captured signatures (rendered whenever the record carries any) */}
          {!documentStyle && (item.tradesperson_sig || item.supervisor_sig || item.sign_off_signature || item.completed_by_sig) && (
            <ExistingSignatures item={item} />
          )}

          {/* Amend times — supervisor tools to correct down/up timestamps.
              Duration is auto-computed the moment both times are valid. */}
          {amendConfig && (
            <AmendTimesPanel
              config={amendConfig}
              item={item}
              onSaved={() => onUpdated?.()}
            />
          )}

          {/* Sign-off actions — hidden entirely once the record is already
              signed, so a supervisor doesn't see a redundant "Sign" prompt
              on an approved breakdown or actioned hazard. */}
          {(signOffConfig || sendBackConfig) && !alreadySigned && (
            <section ref={signoffRef} className="panel-accent p-5" data-testid="signoff-block">
              <div className="flex items-center gap-2 mb-1">
                <PenTool className="w-4 h-4 text-primary-brand" />
                <div className="font-display font-black uppercase tracking-widest text-sm">
                  Supervisor actions
                </div>
              </div>
              <p className="text-sm text-muted mb-4">
                Sign to approve, or send back with a reason.
              </p>

              {!action && (
                <div className="flex flex-wrap gap-2">
                  {signOffConfig && (
                    <button
                      className="btn btn-primary"
                      onClick={() => setAction("sign")}
                      data-testid="btn-open-signoff"
                    >
                      <PenTool className="w-4 h-4" /> {signOffConfig.label || `Sign to ${signOffConfig.verb || "Approve"}`}
                    </button>
                  )}
                  {sendBackConfig && (
                    <button
                      className="btn btn-ghost"
                      onClick={() => setAction("send_back")}
                      data-testid="btn-open-sendback"
                    >
                      <Undo2 className="w-4 h-4" /> Send Back
                    </button>
                  )}
                </div>
              )}

              {action === "sign" && (
                <div className="space-y-4">
                  {signOffConfig?.requireSignature !== false && (
                    <SignaturePad onChange={setSignature} label="Draw signature" />
                  )}
                  <div className="field">
                    <label>{signOffConfig?.noteLabel || "Notes (optional)"}</label>
                    {signOffConfig?.noteType === "input" ? (
                      <input
                        className="input" value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={signOffConfig?.notePlaceholder || "Comments, references, follow-ups…"}
                        data-testid="signoff-notes"
                      />
                    ) : (
                      <textarea
                        className="textarea" rows={2} value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={signOffConfig?.notePlaceholder || "Comments, references, follow-ups…"}
                        data-testid="signoff-notes"
                      />
                    )}
                  </div>
                  <ActionRow busy={busy} onCancel={() => { setAction(null); setSignature(null); }} onSubmit={() => doSubmit("sign")} primaryLabel={signOffConfig?.submitLabel || "Submit sign-off"} primaryIcon={Check} primaryTestid="btn-submit-signoff" disabled={signOffConfig?.requireSignature !== false && !signature} />
                </div>
              )}

              {action === "send_back" && (
                <div className="space-y-4">
                  <div className="field">
                    <label>Reason for sending back</label>
                    <textarea
                      className="textarea" rows={3} required value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="What needs to be fixed / clarified before this can be signed off?"
                      data-testid="sendback-notes"
                    />
                  </div>
                  <ActionRow busy={busy} onCancel={() => { setAction(null); setNotes(""); }} onSubmit={() => doSubmit("send_back")} primaryLabel="Send back" primaryIcon={Undo2} primaryTestid="btn-submit-sendback" tone="danger" disabled={!notes.trim()} />
                </div>
              )}

              {msg && (
                <div
                  className={`mt-3 pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`}
                  data-testid="signoff-status"
                >
                  {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                  {msg.text}
                </div>
              )}
            </section>
          )}

          {/* Raw JSON */}
          <details className="panel-flat p-4" data-testid="raw-json-block">
            <summary className="text-[10px] uppercase tracking-widest text-muted cursor-pointer select-none flex items-center gap-1">
              <FileIcon className="w-3 h-3" /> Raw payload
            </summary>
            <pre className="text-xs font-mono-tight whitespace-pre-wrap break-all p-3 mt-2 rounded-lg max-h-96 overflow-auto"
                 style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
{JSON.stringify(item, null, 2)}
            </pre>
          </details>
        </div>
      </div>

      <EmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        item={item}
        kind={resourceName}
        itemLabel={itemLabel}
      />
    </div>
  );
}

function ActionRow({ busy, onCancel, onSubmit, primaryLabel, primaryIcon: Icon, primaryTestid, tone, disabled }) {
  return (
    <div className="flex gap-2 justify-end">
      <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
      <button
        className={tone === "danger" ? "btn btn-danger" : "btn btn-primary"}
        onClick={onSubmit}
        disabled={busy || disabled}
        data-testid={primaryTestid}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Icon className="w-4 h-4" /> {primaryLabel}</>)}
      </button>
    </div>
  );
}
function ExistingSignatures({ item }) {
  const cells = [];
  const push = (role, sig, name, when) => {
    const src = signatureSrc(sig);
    if (!src && !name) return;
    cells.push({ role, src, name, when });
  };
  push(
    "Tradesperson",
    item.tradesperson_sig,
    item.started_by_name || item.tradesman || item.employee_name || item.created_by_name,
    item.started_at || item.submitted_at || item.completed_at,
  );
  push(
    "Supervisor",
    item.supervisor_sig || item.completed_by_sig,
    item.supervisor_name || item.completed_by_name || item.approver,
    item.approved_at || item.completed_at || item.updated_at,
  );
  push(
    "Sign-off",
    item.sign_off_signature,
    item.sign_off_name,
    item.sign_off_at || item.updated_at,
  );
  if (!cells.length) return null;
  return (
    <section className="panel-flat p-5" data-testid="signatures-panel">
      <div className="flex items-center gap-2 mb-3">
        <div className="font-display font-black uppercase tracking-widest text-sm">Signatures</div>
      </div>
      <div className={`grid gap-3 ${cells.length > 1 ? "md:grid-cols-2" : ""}`}>
        {cells.map((c) => (
          <div
            key={c.role}
            className="rounded-xl overflow-hidden"
            style={{ background: "#fafafa", color: "#111", border: "1px solid #e5e5e5" }}
            data-testid={`sig-cell-${c.role.toLowerCase()}`}
          >
            <div className="px-3 pt-3 text-[10px] uppercase tracking-widest font-black text-neutral-500">
              {c.role}
            </div>
            <div
              className="mx-3 mt-2 rounded-lg flex items-center justify-center"
              style={{ background: "#fff", border: "1px solid #e5e5e5", minHeight: 110 }}
            >
              {c.src ? (
                <img
                  src={c.src}
                  alt={`${c.role} signature`}
                  className="w-full h-auto max-h-40 object-contain"
                  data-testid={`sig-img-${c.role.toLowerCase()}`}
                />
              ) : (
                <div className="py-8 text-neutral-400 text-sm italic">— unsigned —</div>
              )}
            </div>
            <div className="px-3 py-3">
              <div className="font-display font-black text-sm text-neutral-900">{c.name || "—"}</div>
              {c.when && (
                <div className="text-[11px] text-neutral-500 font-mono-tight mt-0.5">
                  {tryDate(c.when)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}



function FactList({ item }) {
  const facts = [];
  const push = (k, v) => v != null && v !== "" && facts.push([k, v]);
  push("Site", item.site_code || item.site);
  push("Asset", item.asset_id || item.asset_name);
  push("Status", item.status || item.stage);
  push("Priority", item.priority || item.severity);
  push("SMU (hrs)", item.smu);
  push("Location", item.location);
  push("Reporter", item.reported_by || item.created_by_name || item.user);
  push("Employee", item.employee_name || item.tradesman_name);
  push("Inspector", item.inspector_name);
  push("Supervisor", item.supervisor_name || item.completed_by_name);
  push("Est. Hours", item.estimated_hours);
  push("People", item.num_people);
  push("Specialist", item.specialist_skill);
  push("Work Order #", item.work_order_number);
  push("From shift", item.from_shift);
  push("To shift", item.to_shift);
  push("Target Completion", tryDate(item.target_completion));
  push("Created", tryDate(item.created_at || item.reported_at || item.ts));
  push("Submitted", tryDate(item.submitted_at));
  push("Completed", tryDate(item.completed_at));
  push("Approved", tryDate(item.approved_at));
  push("Updated", tryDate(item.updated_at));

  // Long-form content the tradesperson / supervisor typed in. Ordered so the
  // most important sections (complaint / cause / corrective / handover notes)
  // appear right below the fact grid.
  const contentSections = [
    ["Fault / Description",  item.fault || item.description],
    ["Complaint",            item.complaint],
    ["Cause",                item.cause],
    ["Corrective action",    item.corrective_action],
    ["Comments",             item.comments],
    ["Handover notes",       item.handover_notes],
    ["Outstanding items",    item.outstanding_items],
    ["Priorities",           item.priorities],
    ["Attention needed",     item.attention_needed],
    ["Subject",              item.subject],
    ["Summary",              item.summary],
    ["Notes",                item.notes],
    ["Resolution",           item.resolution],
    ["Follow-up",            item.follow_up || item.followup],
  ].filter(([, v]) => v != null && v !== "" && !Array.isArray(v) && typeof v !== "object");

  // Any *other* string field > 20 chars that wasn't already surfaced.
  const shownKeys = new Set([
    "fault", "description", "complaint", "cause", "corrective_action",
    "comments", "handover_notes", "outstanding_items", "priorities",
    "attention_needed", "subject", "summary", "notes", "resolution",
    "follow_up", "followup", "sent_back_reason",
    "site_code", "site", "asset_id", "asset_name", "status", "stage",
    "priority", "severity", "smu", "location", "reported_by",
    "created_by_name", "user", "employee_name", "tradesman_name",
    "inspector_name", "supervisor_name", "completed_by_name",
    "estimated_hours", "num_people", "specialist_skill",
    "work_order_number", "from_shift", "to_shift", "target_completion",
    "created_at", "reported_at", "ts", "submitted_at", "completed_at",
    "approved_at", "updated_at",
    "id", "_id", "template_id", "template_name", "type", "kind",
    "part_name", "part_number", "quantity", "subcategory",
    "breakdown_time", "back_in_service_time", "downtime_duration",
    "started_at", "finished_at", "sign_off_at",
    "tradesperson_sig", "supervisor_sig", "completed_by_sig",
    "sign_off_signature", "signature_image", "signature",
    "photos", "photo_urls", "images", "attachments", "photo_url",
    "pdf_url", "document_url", "report_url", "attachment_url",
    "documents", "items", "checklist", "responses", "answers",
    "questions", "fields", "entries", "template_items",
    "parts_needed", "tooling_required", "other_requirements",
    "human_resources_list", "human_resources", "extras", "sections",
    "items_state", "is_finalised", "approved", "sign_off_name",
  ]);
  const extraSections = [];
  for (const [k, v] of Object.entries(item || {})) {
    if (shownKeys.has(k)) continue;
    if (v == null || v === "") continue;
    if (typeof v !== "string") continue;
    if (v.length < 20) continue;
    // Skip data URLs & very long URLs.
    if (/^data:|^https?:\/\//i.test(v) && v.length > 120) continue;
    // Skip UUID-shaped identifiers — they add clutter without information.
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) continue;
    // Skip long hex/base64 blobs that aren't human-readable.
    if (/^[A-Za-z0-9+/=_-]{40,}$/.test(v)) continue;
    // Skip keys that are just identifiers.
    if (/(^|_)id$|^_id$|_id_|(^|_)(uuid|guid|hash|token|key|secret)($|_)/i.test(k)) continue;
    extraSections.push([niceLabel(k), v]);
  }

  const hasBreakdownTimes = item.breakdown_time || item.back_in_service_time || item.downtime_duration;
  const hasPartsInfo = item.part_name || item.part_number || item.quantity;

  if (!facts.length && !contentSections.length && !extraSections.length && !hasBreakdownTimes && !hasPartsInfo) return null;

  return (
    <div className="panel-flat p-4 space-y-4">
      {facts.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {facts.map(([k, v]) => (
            <div key={k}>
              <div className="text-[10px] uppercase tracking-widest text-muted">{k}</div>
              <div className="font-mono-tight text-sm mt-0.5 truncate" title={String(v)}>{String(v)}</div>
            </div>
          ))}
        </div>
      )}

      {hasBreakdownTimes && (
        <DowntimeTile
          down={item.breakdown_time || item.started_at}
          up={item.back_in_service_time || item.finished_at}
          duration={item.downtime_duration}
        />
      )}

      {contentSections.map(([label, value]) => (
        <ContentBlock key={label} label={label} value={value} />
      ))}

      {hasPartsInfo && (
        <PartsCard item={item} />
      )}

      {Array.isArray(item.parts_needed) && item.parts_needed.length > 0 && (
        <ArrayBlock title="Parts needed" items={item.parts_needed} />
      )}
      {Array.isArray(item.tooling_required) && item.tooling_required.length > 0 && (
        <ArrayBlock title="Tooling required" items={item.tooling_required} />
      )}
      {Array.isArray(item.other_requirements) && item.other_requirements.length > 0 && (
        <ArrayBlock title="Other requirements" items={item.other_requirements} />
      )}
      {Array.isArray(item.human_resources_list) && item.human_resources_list.length > 0 && (
        <ArrayBlock title="Human resources" items={item.human_resources_list} />
      )}
      {item.human_resources && !Array.isArray(item.human_resources_list) && (
        <ContentBlock label="Human resources" value={String(item.human_resources)} />
      )}

      {extraSections.length > 0 && (
        <div className="pt-2" style={{ borderTop: "1px solid var(--border)" }}>
          <div className="text-[10px] uppercase tracking-widest text-muted mb-2">Additional details</div>
          <div className="space-y-3">
            {extraSections.map(([label, value]) => (
              <ContentBlock key={label} label={label} value={value} muted />
            ))}
          </div>
        </div>
      )}

      {item.sent_back_reason && (
        <div
          className="pt-2 pb-2 pill pill-red !py-2 !px-3 justify-start !text-xs"
          style={{ borderTop: "1px solid var(--border)" }}
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          Sent back: {item.sent_back_reason}
        </div>
      )}
    </div>
  );
}

function ContentBlock({ label, value, muted }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-black" style={{ color: muted ? "var(--muted)" : "var(--primary)" }}>
        {label}
      </div>
      <div
        className={`text-sm mt-1 whitespace-pre-wrap leading-relaxed rounded-lg px-3 py-2 ${muted ? "text-muted" : ""}`}
        style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
      >
        {String(value)}
      </div>
    </div>
  );
}

function DowntimeTile({ down, up, duration }) {
  const computed = (() => {
    if (duration) return duration;
    if (!down || !up) return null;
    const d1 = new Date(String(down).replace(" ", "T"));
    const d2 = new Date(String(up).replace(" ", "T"));
    if (isNaN(d1) || isNaN(d2) || d2 <= d1) return null;
    const mins = Math.round((d2 - d1) / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  })();
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="drawer-downtime">
      <TileCell label="Down at" value={tryDate(down) || "—"} />
      <TileCell label="Back in service" value={tryDate(up) || "—"} />
      <TileCell label="Downtime" value={computed || "—"} accent />
    </div>
  );
}
function TileCell({ label, value, accent }) {
  return (
    <div className="rounded-xl px-3 py-2.5"
      style={{
        background: accent ? "rgba(245,130,32,0.08)" : "var(--surface-2)",
        border: `1px solid ${accent ? "rgba(245,130,32,0.25)" : "var(--border)"}`,
      }}
    >
      <div className="text-[10px] uppercase tracking-widest font-black"
        style={{ color: accent ? "var(--primary)" : "var(--muted)" }}
      >
        {label}
      </div>
      <div className={`font-mono-tight text-sm mt-0.5 ${accent ? "text-white" : ""}`}>{String(value)}</div>
    </div>
  );
}

function PartsCard({ item }) {
  const rows = [
    ["Part name",   item.part_name],
    ["Part number", item.part_number],
    ["Quantity",    item.quantity],
    ["Subcategory", item.subcategory],
  ].filter(([, v]) => v != null && v !== "");
  if (!rows.length) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-black mb-2" style={{ color: "var(--primary)" }}>
        Parts used
      </div>
      <div
        className="rounded-lg grid grid-cols-2 md:grid-cols-4 gap-3 px-3 py-2.5"
        style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
      >
        {rows.map(([k, v]) => (
          <div key={k}>
            <div className="text-[10px] uppercase tracking-widest text-muted">{k}</div>
            <div className="font-mono-tight text-sm mt-0.5">{String(v)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function niceLabel(key) {
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isBucketedAssetItem(a) {
  if (!a) return false;
  const cat = String(a.category || a.asset_type || "").toLowerCase();
  return /dig|excav|loader|dozer|shovel|drag/.test(cat);
}

function AmendTimesPanel({ config, item, onSaved }) {
  const dtToLocal = (v) => {
    if (!v) return "";
    // Accept "YYYY-MM-DD HH:mm" or ISO — normalise to <input type=datetime-local> format.
    const d = new Date(String(v).replace(" ", "T"));
    if (isNaN(d)) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [down, setDown] = useState(dtToLocal(item.breakdown_time || item.started_at));
  const [up, setUp] = useState(dtToLocal(item.back_in_service_time || item.finished_at || item.approved_at));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const duration = (() => {
    if (!down || !up) return null;
    const d1 = new Date(down);
    const d2 = new Date(up);
    if (isNaN(d1) || isNaN(d2) || d2 <= d1) return null;
    const mins = Math.round((d2 - d1) / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  })();

  const save = async () => {
    if (!down || !up) { setMsg({ type: "err", text: "Fill both times." }); return; }
    if (new Date(up) <= new Date(down)) {
      setMsg({ type: "err", text: "Back-in-service must be after down time." }); return;
    }
    setBusy(true); setMsg(null);
    try {
      const url = config.endpoint(item);
      const toWire = (v) => v.replace("T", " ");
      // Josh (Feb 26): supervisors shouldn't have to justify a time
      // correction — but the prod API still requires a `reason` field on
      // /amend. Send a neutral stub when the input is blank so the amend
      // actually persists (empty payloads used to 422 silently, leaving the
      // report + PDF showing the old times).
      const body = {
        reason: reason.trim() || "Times corrected via portal",
        changes: {
          breakdown_time: toWire(down),
          back_in_service_time: toWire(up),
        },
      };
      await api.post(url, body);
      setMsg({ type: "ok", text: `Times amended · downtime ${duration || "updated"}.` });
      setReason("");
      onSaved?.();
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally { setBusy(false); }
  };

  return (
    <section className="panel-accent p-5" data-testid="amend-times-block">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardCheck className="w-4 h-4 text-primary-brand" />
        <div className="font-display font-black uppercase tracking-widest text-sm">
          Amend times
        </div>
      </div>
      <p className="text-sm text-muted mb-4">
        Correct the down/up timestamps below and downtime recalculates automatically.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="field !mb-0">
          <label className="text-[10px]">Down at</label>
          <input
            className="input"
            type="datetime-local"
            value={down}
            onChange={(e) => setDown(e.target.value)}
            data-testid="amend-down"
          />
        </div>
        <div className="field !mb-0">
          <label className="text-[10px]">Back in service</label>
          <input
            className="input"
            type="datetime-local"
            value={up}
            onChange={(e) => setUp(e.target.value)}
            data-testid="amend-up"
          />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-3 flex-wrap">
        <div className="pill !text-[11px] !py-1 !px-2.5 font-black tabular-nums" data-testid="amend-duration">
          <ClipboardCheck className="w-3 h-3" style={{ color: "var(--primary)" }} />
          {duration ? <>Downtime: <span className="ml-1" style={{ color: "var(--primary)" }}>{duration}</span></> : "Enter valid times"}
        </div>
        {item.downtime_duration && (
          <div className="pill !text-[10px] !py-1 !px-2.5 font-mono-tight">
            Previous: {item.downtime_duration}
          </div>
        )}
      </div>
      <div className="field mt-3 !mb-0">
        <label className="text-[10px]">Reason for amendment <span className="text-white/40">(optional)</span></label>
        <input
          className="input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Leave blank if no note needed"
          data-testid="amend-reason"
        />
      </div>
      <div className="flex justify-end mt-3">
        <button
          className="btn btn-primary btn-sm"
          onClick={save}
          disabled={busy}
          data-testid="btn-submit-amend"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
          Save amended times
        </button>
      </div>
      {msg && (
        <div
          className={`mt-3 pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`}
          data-testid="amend-status"
        >
          {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
          {msg.text}
        </div>
      )}
    </section>
  );
}


function ArrayBlock({ title, items }) {
  return (
    <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--border)" }}>
      <div className="text-[10px] uppercase tracking-widest text-muted mb-2">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => {
          const label =
            typeof it === "string"
              ? it
              : it?.name || it?.label || it?.description || it?.text || JSON.stringify(it);
          return (
            <span
              key={i}
              className="pill !text-[11px] !py-1 !px-2.5 whitespace-normal max-w-full"
              style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
            >
              {label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function ItemStatusPill({ v }) {
  const s = String(v ?? "").toLowerCase();
  if (["pass", "ok", "yes", "true", "compliant"].includes(s)) return <span className="pill pill-green">Pass</span>;
  if (["fail", "no", "false", "non-compliant"].includes(s)) return <span className="pill pill-red-solid">Fail</span>;
  if (["n/a", "na", "skip"].includes(s)) return <span className="pill">N/A</span>;
  if (["warn", "attention", "medium"].includes(s)) return <span className="pill pill-orange">⚠</span>;
  return null;
}

function detectChecklist(item) {
  const candidates = [
    item?.items, item?.checklist, item?.responses, item?.answers,
    item?.questions, item?.fields, item?.entries, item?.template_items,
  ];
  for (const c of candidates) {
    if (Array.isArray(c) && c.length && typeof c[0] === "object") return c;
  }
  return [];
}
function collectPhotos(item) {
  const arr = [];
  if (Array.isArray(item?.photos)) arr.push(...item.photos);
  if (Array.isArray(item?.photo_urls)) arr.push(...item.photo_urls);
  if (Array.isArray(item?.images)) arr.push(...item.images);
  if (Array.isArray(item?.attachments)) arr.push(...item.attachments.filter((a) => (a?.type || a?.url || "").match(/(image|png|jpe?g|gif|webp)/i)));
  if (item?.photo_url) arr.push(item.photo_url);
  return arr;
}
function collectPdfs(item) {
  const arr = [];
  const push = (v, name) => v && arr.push(typeof v === "string" ? { url: v, name: name || "Document" } : v);
  push(item?.pdf_url, "Inspection PDF");
  push(item?.document_url, "Document");
  push(item?.report_url, "Report");
  push(item?.attachment_url, "Attachment");
  if (Array.isArray(item?.attachments)) {
    item.attachments.forEach((a) => {
      const url = a?.url || a?.data;
      if (url && (/pdf/i.test(url) || /pdf/i.test(a?.type || ""))) arr.push(a);
    });
  }
  if (Array.isArray(item?.documents)) arr.push(...item.documents);
  return arr;
}
function formatAnswer(v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
function tryDate(v) { if (!v) return null; try { return new Date(v).toLocaleString(); } catch { return String(v); } }
